import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { auth, db, storage } from "../../backend/firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  arrayUnion,
  doc,
  setDoc,
  collection,
  addDoc,
} from "firebase/firestore";
import ClipLoader from "react-spinners/ClipLoader";
import "./AddDataset.css";

// --- ICONS (Unified with UpdateDataset) ---
const FolderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#667eea" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2-2v11z"/>
  </svg>
);

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
    <polyline points="13 2 13 9 20 9"></polyline>
  </svg>
);

const ChevronRight = ({ isOpen }) => (
  <svg 
    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
  >
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

// --- RECURSIVE TREE NODE COMPONENT ---
const FileTreeNode = ({ node, level, onRemoveFile }) => {
  const [isOpen, setIsOpen] = useState(level < 1); // Auto-expand root
  const isFolder = node.type === 'folder';

  return (
    <div className="tree-node-container">
      <div 
        className="tree-node-row" 
        style={{ paddingLeft: `${level * 20}px` }}
        onClick={() => isFolder && setIsOpen(!isOpen)}
      >
        <div className="tree-node-arrow">
          {isFolder && <ChevronRight isOpen={isOpen} />}
        </div>
        
        <div className="tree-icon">
          {isFolder ? <FolderIcon /> : <FileIcon />}
        </div>

        <span className="tree-name">
          {node.name}
          {isFolder && <span className="tree-count">({node.totalFiles} files)</span>}
        </span>

        {!isFolder && (
          <span 
            className="tree-remove-btn" 
            onClick={(e) => { e.stopPropagation(); onRemoveFile(node.id); }}
            title="Remove File"
          >
            ×
          </span>
        )}
      </div>

      {isFolder && isOpen && (
        <div className="tree-children">
          {node.children.map(child => (
            <FileTreeNode 
              key={child.path} 
              node={child} 
              level={level + 1} 
              onRemoveFile={onRemoveFile}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AddDataset = () => {
  const { id } = useParams();
  const navigation = useNavigate();

  // --- DATASET BASICS ---
  const [datasetName, setDatasetName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState([]);
  const [tagsInput, setTagsInput] = useState("");

  // --- LICENSE ---
  const [licenseType, setLicenseType] = useState("");
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseFileName, setLicenseFileName] = useState("");

  // --- FILES ---
  const [selectedFiles, setSelectedFiles] = useState([]); 
  const [rootFolderName, setRootFolderName] = useState("");

  // --- METADATA ---
  const [source, setSource] = useState("");
  const [collectionMethod, setCollectionMethod] = useState("");
  const [geographicCoverage, setGeographicCoverage] = useState("");

  // --- UI STATE ---
  const [uuid, setUuid] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [visibilityOption, setVisibilityOption] = useState("Public");

  const visibility = ["Public", "Private"];

  useEffect(() => {
    setUuid(uuidv4());
  }, []);

  // --- TREE STRUCTURE CALCULATION ---
  const fileTree = useMemo(() => {
    const tree = [];
    const findNode = (nodes, name) => nodes.find(n => n.name === name && n.type === 'folder');

    selectedFiles.forEach(file => {
      // webkitRelativePath looks like: "RootFolder/Sub/File.txt"
      const parts = file.relativePath.split('/');
      let currentLevel = tree;
      let currentPath = "";

      parts.forEach((part, index) => {
        const isLast = index === parts.length - 1;
        currentPath += part + (isLast ? "" : "/");

        if (isLast) {
          // File
          currentLevel.push({
            name: part,
            path: currentPath,
            type: 'file',
            id: file.id,
            totalFiles: 1
          });
        } else {
          // Folder
          let folderNode = findNode(currentLevel, part);
          if (!folderNode) {
            folderNode = {
              name: part,
              path: currentPath,
              type: 'folder',
              children: [],
              totalFiles: 0
            };
            currentLevel.push(folderNode);
          }
          currentLevel = folderNode.children;
        }
      });
    });

    // Stats and Sorting
    const processStats = (nodes) => {
      let total = 0;
      nodes.forEach(node => {
        if (node.type === 'folder') {
          node.totalFiles = processStats(node.children);
          total += node.totalFiles;
        } else {
          total += 1;
        }
      });
      return total;
    };
    processStats(tree);

    const sortNodes = (nodes) => {
      nodes.sort((a, b) => {
        if (a.type === b.type) return a.name.localeCompare(b.name);
        return a.type === 'folder' ? -1 : 1;
      });
      nodes.forEach(n => { if (n.children) sortNodes(n.children); });
    };
    sortNodes(tree);

    return tree;
  }, [selectedFiles]);

  // --- HANDLERS ---

  const handleLicenseFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setLicenseFile(selectedFile);
      setLicenseFileName(selectedFile.name);
    }
  };

  const handleFolderChange = (e) => {
    const fileList = e.target.files;
    const files = Array.from(fileList);
    if (files.length === 0) return;

    // Detect Root
    const firstPath = files[0].webkitRelativePath || files[0].name;
    const detectedRoot = firstPath.split('/')[0];
    
    // Safety check: Ensure strict single root
    const hasMultipleRoots = files.some(f => (f.webkitRelativePath || f.name).split('/')[0] !== detectedRoot);
    
    if (hasMultipleRoots) {
        alert("Error: Multiple root folders detected. Please upload a single folder.");
        return;
    }

    setRootFolderName(detectedRoot);

    const newEntries = files.map((file) => {
      const relativePath = file.webkitRelativePath || file.name;
      return {
        file: file,
        fileName: file.name,
        title: relativePath, 
        relativePath: relativePath,
        id: Date.now() + Math.random(),
      };
    });

    setSelectedFiles(newEntries);
    e.target.value = "";
  };

  const removeSubFile = (id) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
  };

  const addTag = () => {
    if (tagsInput.trim() && !tags.includes(tagsInput.trim())) {
      setTags([...tags, tagsInput.trim()]);
      setTagsInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  // --- SUBMIT LOGIC ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!datasetName || selectedFiles.length === 0) {
      alert("Please provide a dataset name and upload a folder.");
      return;
    }

    if (licenseType && licenseType !== "No License" && !licenseFile) {
      alert(`Please upload the official documentation/terms for the ${licenseType} license.`);
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setCurrentFileIndex(0);

    try {
      let licenseDocUrl = "";

      // 1. Upload License
      if (licenseFile) {
        const licenseStorageRef = ref(storage, `datasets/${uuid}/license_docs/${licenseFile.name}`);
        licenseDocUrl = await new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(licenseStorageRef, licenseFile);
          uploadTask.on(
            "state_changed",
            null,
            (error) => reject(error),
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });
      }

      // 2. Batch Upload Files
      const uploadedFiles = [];
      const totalFiles = selectedFiles.length;

      for (let i = 0; i < totalFiles; i++) {
        setCurrentFileIndex(i + 1);
        const dataset = selectedFiles[i];
        
        const storagePath = `datasets/${uuid}/${dataset.relativePath}`;
        const storageRef = ref(storage, storagePath);

        const publicURL = await new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, dataset.file);
          uploadTask.on(
            "state_changed",
            null, 
            (error) => {
              console.error(`Error uploading ${dataset.fileName}:`, error);
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });

        const fileExt = dataset.fileName.split(".").pop();

        uploadedFiles.push({
          title: dataset.title,
          fileName: dataset.fileName,
          fileUrl: publicURL,
          fileFormat: fileExt,
          uploadedAt: new Date().toISOString(),
          path: dataset.relativePath 
        });
        
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }

      // 3. Save Dataset Doc
      await setDoc(
        doc(db, "datasets", uuid),
        {
          author: id,
          lastUpdated: new Date().toISOString(),
          uuid,
          name: datasetName || " ",
          description,
          source,
          collectionMethod,
          tags: tags ? tags.map((tag) => tag.trim()) : [],
          category,
          license: licenseType,
          license_doc_url: licenseDocUrl,
          geographicCoverage,
          fileCount: selectedFiles.length,
          latestVersion: "1.0.0",
          downloads: 0,
          visibility: visibilityOption,
          access_users:
            visibilityOption === "Public"
              ? []
              : arrayUnion(auth.currentUser.uid),
          searchQuery: datasetName.split(" "),
        },
        { merge: true }
      );

      // 4. Save to 'files' subcollection
      for (const uploadedFile of uploadedFiles) {
        await addDoc(collection(db, "datasets", uuid, "files"), {
          ...uploadedFile,
          downloads: 0,
        });
      }

      // 5. Save 'version' document
      await setDoc(doc(db, "datasets", uuid, "versions", "1.0.0"), {
        files: uploadedFiles,
        publishedOn: new Date().toISOString(),
        downloads: 0,
        fileCount: uploadedFiles.length,
        isDisabled: false
      });

      alert("Dataset uploaded successfully!");
      navigation(-1);
    } catch (error) {
      console.error("Error:", error.message);
      alert("Something went wrong while uploading or saving data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-dataset-page-container">
      <div className="add-dataset-page-content">
        <div className="add-dataset-form-header">
          <p className="add-dataset-form-subtitle">
            Share your data with the community or keep it private
          </p>
        </div>

        <form onSubmit={handleSubmit} className="add-dataset-main-form">
          <div className="add-dataset-form-grid">
            
            {/* --- LEFT COLUMN: METADATA & INFO --- */}
            <div className="add-dataset-form-column">
              <div className="add-dataset-form-field">
                <span className="add-dataset-form-label">Dataset Name *</span>
                <input
                  type="text"
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  placeholder="Enter a descriptive name"
                  required
                  className="add-dataset-form-input"
                />
              </div>

              <div className="add-dataset-form-field">
                <span className="add-dataset-form-label">Description</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the purpose, contents, and potential use cases..."
                  rows={4}
                  className="add-dataset-form-textarea"
                />
              </div>

              <div className="add-dataset-form-row">
                <div className="add-dataset-form-field">
                  <span className="add-dataset-form-label">Category</span>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="add-dataset-form-select"
                  >
                    <option value="">Select category...</option>
                    <option>Economy & Finance</option>
                    <option>Health</option>
                    <option>Agriculture</option>
                    <option>Technology</option>
                    <option>Computer Vision</option>
                    <option>Audio & Speech</option>
                    <option>NLP</option>
                  </select>
                </div>

                <div className="add-dataset-form-field">
                  <span className="add-dataset-form-label">License</span>
                  <select
                    value={licenseType}
                    onChange={(e) => setLicenseType(e.target.value)}
                    className="add-dataset-form-select"
                  >
                    <option value="">Select license...</option>
                    <option>Open Data Commons</option>
                    <option>CC BY 4.0</option>
                    <option>MIT License</option>
                    <option>No License</option>
                  </select>
                </div>
              </div>

              {licenseType && licenseType !== "No License" && (
                <div className="add-dataset-license-upload-container">
                  <span className="add-dataset-form-label">
                    License Documentation *
                  </span>
                  <div className="license-file-input-wrapper">
                    <input
                      type="file"
                      id="license-doc-upload"
                      accept=".pdf,.txt,.doc,.docx"
                      onChange={handleLicenseFileChange}
                      className="add-dataset-file-input-hidden"
                    />
                    <label htmlFor="license-doc-upload" className="license-file-label">
                      <span className="license-icon">📜</span>
                      <span className="license-filename">
                        {licenseFileName || "Click to upload License Terms (PDF/TXT)"}
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <div className="add-dataset-form-field">
                <span className="add-dataset-form-label">Tags</span>
                <div className="add-dataset-tags-input-container">
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a tag and press Enter"
                    className="add-dataset-form-input"
                  />
                  <button type="button" onClick={addTag} className="add-dataset-tag-add-button">
                    Add
                  </button>
                </div>
                <div className="add-dataset-tags-container">
                  {tags.map((tag, index) => (
                    <div key={index} className="add-dataset-tag-item">
                      <span className="add-dataset-tag-text">{tag}</span>
                      <span onClick={() => removeTag(tag)} className="add-dataset-tag-remove">×</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* --- RIGHT COLUMN: TECH DATA & FILES --- */}
            <div className="add-dataset-form-column">
              <div className="add-dataset-form-section">
                <h3 className="add-dataset-section-title">Technical Metadata</h3>
                <div className="add-dataset-form-row">
                    <div className="add-dataset-form-field-tech">
                        <span className="add-dataset-form-label-tech">Source</span>
                        <input className="add-dataset-form-input" value={source} onChange={e=>setSource(e.target.value)} />
                    </div>
                    <div className="add-dataset-form-field-tech">
                        <span className="add-dataset-form-label-tech">Collection Method</span>
                        <input className="add-dataset-form-input" value={collectionMethod} onChange={e=>setCollectionMethod(e.target.value)} />
                    </div>
                </div>
                <div className="add-dataset-form-field-tech">
                    <span className="add-dataset-form-label-tech">Visibility</span>
                    <div className="add-dataset-visibility-options">
                        {visibility.map((option) => (
                        <span
                            key={option}
                            style={{ backgroundColor: visibilityOption === option ? "rgba(102, 126, 234, 0.2)" : "rgba(255, 255, 255, 0.05)", borderColor: visibilityOption === option ? "#667eea" : "rgba(255,255,255,0.1)" }}
                            className="add-dataset-visibility-option"
                            onClick={() => setVisibilityOption(option)}
                        >
                            {option}
                        </span>
                        ))}
                    </div>
                </div>
              </div>

              {/* FILE UPLOAD & TREE VIEW */}
              <div className="add-dataset-form-section">
                <h3 className="add-dataset-section-title">Data Upload</h3>
                
                {/* 1. Upload Button */}
                <div className="add-dataset-form-field">
                  <div className="add-dataset-file-upload-area">
                    <input
                      type="file"
                      webkitdirectory="" 
                      directory=""
                      multiple
                      onChange={handleFolderChange}
                      className="add-dataset-file-input"
                      id="add-dataset-folder-upload"
                    />
                    <label htmlFor="add-dataset-folder-upload" className="add-dataset-file-upload-label folder-style">
                      <div className="add-dataset-file-upload-content">
                        <span className="add-dataset-file-upload-icon">📁</span>
                        <span className="add-dataset-file-upload-text">
                           {rootFolderName ? "Change Folder" : "Select Root Folder"}
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* 2. Root Name Indicator */}
                {rootFolderName && (
                   <div className="root-indicator">
                        <span className="root-label">Root:</span>
                        <span className="root-value">{rootFolderName}/</span>
                   </div>
                )}

                {/* 3. Tree View */}
                <div className="add-dataset-files-list tree-view-wrapper">
                    {fileTree.length > 0 ? (
                        fileTree.map(node => (
                            <FileTreeNode 
                                key={node.path} 
                                node={node} 
                                level={0} 
                                onRemoveFile={removeSubFile}
                            />
                        ))
                    ) : (
                        <div className="empty-tree-state">
                            Please upload a folder containing your dataset files.
                        </div>
                    )}
                </div>
                
                <div className="add-dataset-files-count">
                  {selectedFiles.length} file(s) staged for upload
                </div>
              </div>

            </div>
          </div>

          <div className="add-dataset-form-actions">
            <button
              type="submit"
              disabled={loading || !datasetName || selectedFiles.length === 0}
              className="add-dataset-submit-button"
            >
              {loading ? (
                <>
                  <ClipLoader size={16} color="#ffffff" />
                  <span>Uploading {currentFileIndex}/{selectedFiles.length}... ({uploadProgress}%)</span>
                </>
              ) : (
                `Upload Dataset (${selectedFiles.length} files)`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDataset;